import { Coordinate, Trip } from "@fairfleet/geotab";
import { useQuery } from "@tanstack/react-query";
import { sub } from "date-fns";
import { useMemo } from "react";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { coordinateKey, useCoordinateAddresses } from "@/hooks/addresses/useCoordinateAddresses";
import { chunkArray } from "@/utilities/chunkArray";
import { fetchTripsComplete, GetTrips, SEARCHES_PER_BATCH } from "@/utilities/query/fetchTripsComplete";

/**
 * How far before a vehicle's first listed trip its predecessor is searched, in stages: a short
 * window for everyone, the long one only for vehicles that stood still longer. The audit already
 * loads a lead week, so only long-idle vehicles get past the first stage there.
 */
export const LOOKBACK_STAGE_DAYS = [2, 30];

/**
 * The trip fields address resolution reads; satisfied by both Trip and FilteredTrip.
 * `nextTripStart` is optional so callers that only carry an export-shaped trip (which never
 * needs to look up its own link) can still pass their value through `getStartAddress`/
 * `getStopAddress` — it is only read while building the lookup, from the full trip list.
 */
export interface AddressTrip {
  id: string;
  start: Date;
  stopPoint?: unknown;
  nextTripStart?: Date;
  device: { id: string };
}

/**
 * Resolves the full start/stop addresses for a list of trips in a single batched
 * geocoding request, so the values are available synchronously for grid export.
 * A trip's start address is the stop address of the previous trip for the same device.
 *
 * @param trips - The trips whose addresses are geocoded.
 * @param allTrips - Every trip of the same devices and period, used to find each trip's
 * predecessor. Defaults to `trips`; pass the full list when `trips` is a subset. Trips loaded
 * from before the period (a lead window) spare the lookback query for the first trip.
 * @param options.enabled - False looks nothing up (no geocoding, no lookback) and reports
 * ready: for callers whose address columns are hidden, or whose trips are still loading.
 */
export function useTripAddresses(
  trips: AddressTrip[],
  allTrips: AddressTrip[] = trips,
  { enabled = true }: { enabled?: boolean } = {},
) {
  const { inRangePreviousStopPointByTripId, unlinkedFirstTripIds } = useMemo(() => {
    // Nothing is looked up while disabled; the audit passes a list that grows with every vehicle
    // that finishes loading and must not be grouped and sorted before it is complete.
    if (!enabled) return { inRangePreviousStopPointByTripId: new Map<string, unknown>(), unlinkedFirstTripIds: new Set<string>() };
    const unlinkedFirstTripIds = new Set<string>();
    const tripsByDevice = new Map<string, AddressTrip[]>();
    for (const trip of allTrips) {
      const deviceTrips = tripsByDevice.get(trip.device.id) ?? [];
      deviceTrips.push(trip);
      tripsByDevice.set(trip.device.id, deviceTrips);
    }

    const result = new Map<string, unknown>();
    for (const deviceTrips of tripsByDevice.values()) {
      const nextTripStartToTrip = new Map<number, AddressTrip>();
      for (const trip of deviceTrips) {
        if (trip.nextTripStart) {
          nextTripStartToTrip.set(trip.nextTripStart.getTime(), trip);
        }
      }

      deviceTrips.sort((a, b) => a.start.getTime() - b.start.getTime());
      for (let i = 0; i < deviceTrips.length; i++) {
        const trip = deviceTrips[i];
        const linkedTrip = nextTripStartToTrip.get(trip.start.getTime());
        if (linkedTrip && linkedTrip.id !== trip.id) {
          result.set(trip.id, linkedTrip.stopPoint);
        } else if (i === 0) {
          unlinkedFirstTripIds.add(trip.id);
        } else {
          // Only adjacent when the previous trip's own link does not point somewhere else: a
          // driver-filtered list skips the other drivers' trips in between.
          const previous = deviceTrips[i - 1];
          if (!previous.nextTripStart || previous.nextTripStart.getTime() === trip.start.getTime()) {
            result.set(trip.id, previous.stopPoint);
          }
        }
      }
    }
    return { inRangePreviousStopPointByTripId: result, unlinkedFirstTripIds };
  }, [enabled, allTrips]);

  // Only the trips that need an address are looked up, not the first trip of every device in a
  // lead window.
  const unlinkedFirstTrips = useMemo(
    () => (enabled ? trips.filter(trip => unlinkedFirstTripIds.has(trip.id)) : []),
    [enabled, trips, unlinkedFirstTripIds],
  );

  // A vehicle's first trip in the range has no predecessor in the list; its previous trip lies
  // before the range, so it is looked up separately.
  const { geotabClient, activeSessionId } = useGeotabClient();
  const lookbackKey = unlinkedFirstTrips.map(t => `${t.device.id}@${t.start.getTime()}`).join(",");
  const lookback = useQuery({
    queryKey: ["tripStartLookback", activeSessionId, lookbackKey],
    enabled: unlinkedFirstTrips.length > 0,
    queryFn: async ({ signal }) => {
      const get = (search: Parameters<GetTrips>[0], resultsLimit: number) =>
        geotabClient.get("Trip", search, resultsLimit, signal) as Promise<Trip[]>;
      const result = new Map<string, unknown>();
      // Staged: most predecessors lie within a day or two, so a short window comes first and
      // the long one is searched only for the vehicles still missing a predecessor. One window
      // per vehicle, ending where its first listed trip starts.
      let missing = unlinkedFirstTrips;
      for (const days of LOOKBACK_STAGE_DAYS) {
        const stillMissing: AddressTrip[] = [];
        for (const batch of chunkArray(missing, SEARCHES_PER_BATCH)) {
          await Promise.all(
            batch.map(async trip => {
              const candidates = await fetchTripsComplete(get, {
                fromDate: sub(trip.start, { days }),
                toDate: trip.start,
                searches: [{ deviceSearch: { id: trip.device.id } }],
                signal,
              });
              const previous = candidates.find(
                c => c.id !== trip.id && c.nextTripStart?.getTime() === trip.start.getTime(),
              );
              if (previous) result.set(trip.id, previous.stopPoint);
              else stillMissing.push(trip);
            }),
          );
        }
        missing = stillMissing;
        if (missing.length === 0) break;
      }
      return result;
    },
  });

  const previousStopPointByTripId = useMemo(
    () => new Map([...inRangePreviousStopPointByTripId, ...(lookback.data ?? [])]),
    [inRangePreviousStopPointByTripId, lookback.data],
  );

  const coordinatesByKey = useMemo(() => {
    const map = new Map<string, Coordinate>();
    for (const trip of enabled ? trips : []) {
      const stopKey = coordinateKey(trip.stopPoint);
      if (stopKey) map.set(stopKey, trip.stopPoint as Coordinate);

      const previousStopPoint = previousStopPointByTripId.get(trip.id);
      const startKey = coordinateKey(previousStopPoint);
      if (startKey) map.set(startKey, previousStopPoint as Coordinate);
    }
    return map;
  }, [enabled, trips, previousStopPointByTripId]);

  const {
    data: addressByCoordinateKey,
    isPlaceholderData,
    isError: geocodingFailed,
  } = useCoordinateAddresses(coordinatesByKey);

  function getStopAddress(trip: AddressTrip): string {
    const key = coordinateKey(trip.stopPoint);
    return (key && !isPlaceholderData && addressByCoordinateKey?.get(key)) || "";
  }

  function getStartAddress(trip: AddressTrip): string {
    const key = coordinateKey(previousStopPointByTripId.get(trip.id));
    return (key && !isPlaceholderData && addressByCoordinateKey?.get(key)) || "";
  }

  // False while the coordinates of the current trip list have not resolved yet (still the
  // previous list's placeholder data, or no data at all) — a filed export must not go out with
  // addresses that belong to a vehicle or period the user has since filtered away from.
  // A failed lookback leaves the first trips of a vehicle without a start address, so it counts
  // as an error and never as ready: an export must not go out with those gaps.
  const isError = geocodingFailed || lookback.isError;
  const lookbackPending = unlinkedFirstTrips.length > 0 && lookback.isPending;
  const isReady =
    !enabled ||
    (!isError && !isPlaceholderData && addressByCoordinateKey !== undefined && !lookbackPending);

  return { getStartAddress, getStopAddress, isReady, isError };
}
